import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { softReveal, viewportOnce } from "./motion";

const renderVisual = (type, points) => {
  if (type === "bars") {
    return (
      <div className="service-visual service-visual-bars" aria-hidden="true">
        {points.map((point, index) => (
          <div key={point} className="service-bar-wrap">
            <div className={`service-bar service-bar-${index + 1}`} />
            <span>{point}</span>
          </div>
        ))}
      </div>
    );
  }

  if (type === "line") {
    return (
      <div className="service-visual service-visual-line" aria-hidden="true">
        <div className="service-line-path">
          <span className="service-line-dot service-line-dot-1" />
          <span className="service-line-dot service-line-dot-2" />
          <span className="service-line-dot service-line-dot-3" />
        </div>
        <div className="service-visual-labels">
          {points.map((point) => (
            <span key={point}>{point}</span>
          ))}
        </div>
      </div>
    );
  }

  if (type === "pulse") {
    return (
      <div className="service-visual service-visual-pulse" aria-hidden="true">
        {points.map((point, index) => (
          <div key={point} className={`service-pulse service-pulse-${index + 1}`}>
            <span>{point}</span>
          </div>
        ))}
      </div>
    );
  }

  if (type === "steps") {
    return (
      <div className="service-visual service-visual-steps" aria-hidden="true">
        {points.map((point) => (
          <div key={point} className="service-step">
            <span className="service-step-dot" />
            <span>{point}</span>
          </div>
        ))}
      </div>
    );
  }

  if (type === "network") {
    return (
      <div className="service-visual service-visual-network" aria-hidden="true">
        <div className="service-network-node service-network-center">{points[1]}</div>
        <div className="service-network-node service-network-left">{points[0]}</div>
        <div className="service-network-node service-network-right">{points[2]}</div>
      </div>
    );
  }

  return (
    <div className="service-visual service-visual-funnel" aria-hidden="true">
      {points.map((point, index) => (
        <div key={point} className={`service-funnel-step service-funnel-step-${index + 1}`}>
          <span>{point}</span>
        </div>
      ))}
    </div>
  );
};

const ServiceCard = ({
  icon,
  title,
  desc,
  outcome,
  detail,
  bullets,
  visualLabel,
  visualType,
  visualPoints,
  expanded,
  rootRef,
  onHoverStart,
  onHoverEnd,
  onToggle,
}) => {
  const panelId = `service-panel-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  const handleToggle = () => {
    onToggle?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <motion.article
      ref={rootRef}
      className={`service ${expanded ? "service-expanded" : ""}`}
      variants={softReveal}
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
      transition={{ duration: 0.22 }}
      tabIndex={0}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      aria-expanded={expanded}
      aria-controls={panelId}
    >
      <div className="service-icon" aria-hidden="true">
        {icon}
      </div>
      <h3>{title}</h3>
      <p className="service-summary">{desc}</p>
      <p className="service-outcome">{outcome}</p>

      <button
        type="button"
        className="service-toggle"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={(event) => {
          event.stopPropagation();
          handleToggle();
        }}
      >
        {expanded ? "Close overview" : "Open overview"}
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            id={panelId}
            className="service-panel"
            initial={{ opacity: 0, maxHeight: 0, y: 12 }}
            animate={{ opacity: 1, maxHeight: 520, y: 0 }}
            exit={{ opacity: 0, maxHeight: 0, y: 8 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="service-detail">{detail}</p>

            <div className="service-panel-grid">
              <div className="service-visual-card">
                <p className="service-panel-kicker">{visualLabel}</p>
                {renderVisual(visualType, visualPoints)}
              </div>

              <div className="service-benefits">
                <p className="service-panel-kicker">Included in this service</p>
                <ul>
                  {bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
};

export { renderVisual };
export default ServiceCard;
